import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Drawer, Spin, Upload } from "antd";
import autoAnimate from "@formkit/auto-animate";
import { doc, setDoc } from "firebase/firestore";
import db from "../../../firebase";

// assets
import profileImg from "../../../assets/images/profile-drawer-icon.png";
import phoneIcon from "../../../assets/icons/profile-phone-icon.png";
import { getUserInfo, updateNationalIdImage } from "../../../network";
import { baseUrl } from "../../../services";
import { toastError, toastSuccess } from "../../../helpers/toasters";

const ProfileDrawer = ({ onClose, open, dir, notificationCount }) => {
  const { authorization } = useSelector((state) => state?.auth);
  const { lang } = useSelector((state) => state?.language);
  const { t } = useTranslation();
  const parent = useRef(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (authorization && notificationCount > 0) {
      setDoc(
        doc(db, `users`, authorization?.userId),
        {
          unReadGroupNotification: 0,
        },
        { merge: true },
      );
    }
  }, [authorization, notificationCount]);

  useEffect(() => {
    parent.current && autoAnimate(parent.current);
  }, [parent, parent.current]);

  const fetchUserInfo = (showLoader = true) => {
    if (showLoader) setLoading(true);
    getUserInfo(
      (res) => {
        setUserInfo(res?.data);
        setLoading(false);
        setUploading(false);
      },
      (err) => {
        console.log(err);
        setLoading(false);
        setUploading(false);
      },
    );
  };

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const handleNationalIdUpload = (file) => {
    const formData = new FormData();
    formData.append("IDNumberImage", file);
    setUploading(true);
    updateNationalIdImage(
      formData,
      (res) => {
        if (res?.success) {
          toastSuccess(res?.message);
          fetchUserInfo(false);
        } else {
          setUploading(false);
          toastError(res?.message);
        }
      },
      (err) => {
        setUploading(false);
        toastError(err?.data?.error || err?.data?.message);
      },
    );
    return false;
  };

  const userData = useMemo(() => {
    return {
      fullName: userInfo?.fullName || authorization?.fullName,
      mobile: userInfo?.mobile || authorization?.mobile,
      idNumberImage: userInfo?.idNumberImage,
      idNumberImageStatus: userInfo?.idNumberImageStatus,
      idNumberImageStatusId: userInfo?.idNumberImageStatusId,
    };
  }, [userInfo, authorization]);

  const isRejected = useMemo(() => {
    return userData?.idNumberImageStatusId === 3;
  }, [userData]);

  return (
    <Drawer
      title={
        <p dir={dir} className="mb-0">
          {t("nav.profile")}
        </p>
      }
      placement={dir === "ltr" ? "right" : "left"}
      onClose={onClose}
      open={open}
      className={`main-drawer profile-drawer ${
        dir === "rtl" ? "main-drawer--ar" : ""
      }`}
      maskClosable={false}
      zIndex={1100}
      width={
        window.innerWidth >= 1440
          ? "45%"
          : window.innerWidth >= 768
            ? "50%"
            : "100%"
      }
    >
      {loading ? (
        <div className="h-100 d-flex justify-content-center align-items-center">
          <Spin size="large" />
        </div>
      ) : (
        <div
          dir={dir}
          ref={parent}
          className="profile-drawer__main-holder pt-4 d-flex flex-column"
        >
          <div>
            <img
              src={profileImg}
              alt="profile image"
              className="profile-drawer__profile-icon d-block mx-auto mb-2"
            />
            <h2 className="fsize-5 text-center mb-4">{userData?.fullName}</h2>
            <div className="d-flex justify-content-center align-items-center gap-3">
              <img
                src={phoneIcon}
                alt="phone icon"
                className="profile-drawer__phone-icon"
              />
              <p className="mb-0 fsize-5 fw-bold">{userData?.mobile}</p>
            </div>
          </div>

          {userData?.idNumberImage && (
            <div>
              <div className="d-flex justify-content-between align-items-center profile-drawer__national-id-header">
                <p className="profile-drawer__national-id-header__title mb-0">
                  {t("profileModal.nationalId")}
                </p>
                {isRejected && (
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    disabled={uploading}
                    beforeUpload={handleNationalIdUpload}
                  >
                    <span
                      className={`profile-drawer__national-id-header__upload-new ${
                        uploading ? "opacity-50" : ""
                      }`}
                    >
                      {uploading ? (
                        <Spin size="small" />
                      ) : (
                        t("profileModal.uploadNew")
                      )}
                    </span>
                  </Upload>
                )}
              </div>
              <div className="profile-drawer__national-id-image-holder">
                <img
                  src={baseUrl + userData?.idNumberImage}
                  alt={t("profileModal.nationalId")}
                  loading="eager"
                />
                <span
                  className={`profile-drawer__national-id-image-holder__status profile-drawer__national-id-image-holder__status--${userData?.idNumberImageStatusId} profile-drawer__national-id-image-holder__status--${lang}`}
                >
                  {userData?.idNumberImageStatus}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default ProfileDrawer;
